<template>
    <div class="pagination">
        <div class="pagination__meta">
            <label v-if="showPageSize" class="pagination__page-size">
                <span>Rows per page:</span>
                <select
                    v-model.number="selectedItemsPerPage"
                    class="pagination__field"
                    aria-label="Rows per page"
                >
                    <option v-for="size in pageSizeOptions" :key="size" :value="size">
                        {{ size }}
                    </option>
                </select>
            </label>

            <div aria-live="polite">Page {{ page }} of {{ pageCount }}</div>
        </div>

        <nav class="pagination__pages" aria-label="Table pagination">
            <button
                type="button"
                class="pagination__button pagination__navigation"
                :disabled="page === 1"
                aria-label="Previous page"
                @click="changePage(page - 1)"
            >‹
            </button>

            <template v-for="(item, index) in paginationItems">
                <span
                    v-if="item === 'ellipsis'"
                    :key="`ellipsis-${index}`"
                    class="pagination__ellipsis"
                    aria-hidden="true"
                >…</span>
                <button
                    v-else
                    :key="item"
                    type="button"
                    class="pagination__button"
                    :class="{'pagination__button--active': item === page}"
                    :aria-current="item === page ? 'page' : null"
                    :aria-label="`Page ${item}`"
                    @click="changePage(item)"
                >{{ item }}
                </button>
            </template>

            <button
                type="button"
                class="pagination__button pagination__navigation"
                :disabled="page === pageCount"
                aria-label="Next page"
                @click="changePage(page + 1)"
            >›
            </button>
        </nav>

        <label v-if="showGoToPage" class="pagination__go-to">
            <span>Go to page:</span>
            <input
                :value="page"
                type="number"
                min="1"
                :max="pageCount"
                class="pagination__field pagination__page-input"
                aria-label="Go to page"
                @change="changePage"
            >
        </label>
    </div>
</template>

<script>
export default {
    name: "Pagination",
    props: {
        options: {
            type: Object,
            required: true,
        },
        totalItems: {
            type: Number,
            default: 0,
        },
        pageSizeOptions: {
            type: Array,
            default: () => [10, 20, 30, 50],
        },
        visiblePages: {
            type: Number,
            default: 9,
        },
        showPageSize: {
            type: Boolean,
            default: true,
        },
        showGoToPage: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        page() {
            return this.normalizePositiveInteger(this.options.page, 1);
        },
        itemsPerPage() {
            return this.normalizePositiveInteger(this.options.itemsPerPage, 10);
        },
        selectedItemsPerPage: {
            get() {
                return this.itemsPerPage;
            },
            set(itemsPerPage) {
                this.updateOptions({itemsPerPage, page: 1});
            },
        },
        pageCount() {
            return Math.max(1, Math.ceil(this.totalItems / this.itemsPerPage));
        },
        paginationItems() {
            return this.buildPaginationItems(
                this.page,
                this.pageCount,
                this.visiblePages,
            );
        },
    },
    watch: {
        options: {
            deep: true,
            handler() {
                this.ensureDefaultOptions();
            },
        },
        pageCount() {
            if (this.page > this.pageCount) this.changePage(this.pageCount);
        },
    },
    mounted() {
        this.ensureDefaultOptions();
    },
    methods: {
        ensureDefaultOptions() {
            const page = this.page;
            const itemsPerPage = this.itemsPerPage;

            if (page !== this.options.page || itemsPerPage !== this.options.itemsPerPage) {
                this.updateOptions({page, itemsPerPage});
            }
        },
        updateOptions(values) {
            this.$emit("update:options", {...this.options, ...values});
        },
        changePage(value) {
            const requestedPage = value?.target ? value.target.value : value;
            const nextPage = Math.min(this.pageCount, this.normalizePositiveInteger(requestedPage, 1));

            if (nextPage !== this.page) {
                this.updateOptions({page: nextPage});
            }
        },
        createPageRange(start, end) {
            return Array.from({length: end - start + 1}, (_, index) => start + index);
        },
        normalizePositiveInteger(value, fallback) {
            return Math.max(1, Math.floor(Number(value) || fallback));
        },
        buildPaginationItems(currentPage, totalPages, visiblePages) {
            const minVisiblePages = 5;
            const visibleCount = Math.max(minVisiblePages, this.normalizePositiveInteger(visiblePages));

            if (totalPages <= visibleCount) {
                return this.createPageRange(1, totalPages);
            }

            // With 9 visible items, keep 4 pages fixed at each end.
            const firstPagesCount = Math.floor((visibleCount - 1) / 2);
            const lastPagesCount = visibleCount - firstPagesCount - 1;
            const middleStartPage = firstPagesCount;
            const middleEndPage = totalPages - lastPagesCount + 1;

            if (
                currentPage < middleStartPage ||
                currentPage > middleEndPage
            ) {
                return [
                    ...this.createPageRange(1, firstPagesCount),
                    "ellipsis",
                    ...this.createPageRange(totalPages - lastPagesCount + 1, totalPages),
                ];
            }

            // Center the current page when it is away from the edges.
            const centerPagesCount = visibleCount - 4;
            const centerRangeStart = currentPage - Math.floor(centerPagesCount / 2);

            return [
                1,
                "ellipsis",
                ...this.createPageRange(centerRangeStart, centerRangeStart + centerPagesCount - 1),
                "ellipsis",
                totalPages,
            ];
        },
    },
};
</script>

<style scoped>
.pagination {
    display: flex;
    justify-content: space-between;
    padding: 10px 8px 2px;
    font-size: 14px;
    margin-bottom: 20px;
}

.pagination__meta,
.pagination__page-size,
.pagination__pages,
.pagination__go-to {
    display: flex;
    align-items: center;
}

.pagination__meta {
    gap: 18px;
}

.pagination__page-size,
.pagination__go-to {
    gap: 8px;
    margin-bottom: 0;
}

.pagination__pages {
    gap: 6px;
}

.pagination__field {
    width: 75px;
    height: 36px;
    padding: 4px 8px;
    border: 1px solid #9e9e9e;
    border-radius: 4px;
    font: inherit;
}

.pagination__button {
    min-width: 34px;
    height: 34px;
    padding: 0 8px;
    border: 0;
    background: var(--tenant-color-button-background, #ddd) !important;
    color: var(--tenant-color-button-text, #fff) !important;
    font: inherit;
}

.pagination__button:hover:not(:disabled),
.pagination__button:focus-visible {
    background: var(--tenant-color-button-hover-background, #495057) !important;
    color: var(--tenant-color-button-hover-text, #fff) !important;
}

.pagination__button--active {
    background: var(--tenant-color-button-hover-background, #495057) !important;
    color: var(--tenant-color-button-hover-text, #fff) !important;
}

.pagination__button:disabled {
    opacity: .35;
}

.pagination__navigation {
    font-size: 25px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.pagination__ellipsis {
    min-width: 24px;
    text-align: center;
}

@media (max-width: 1300px) {
    .pagination {
        flex-direction: column;
        gap: 12px;
    }

    .pagination__meta {
        flex-direction: column;
        gap: 8px;
    }

    .pagination__pages {
        justify-content: center;
    }

    .pagination__go-to {
        justify-content: center;
    }
}
</style>
